import React, { useEffect } from 'react';
import { 
  FaChartLine, 
  FaHandshake, 
  FaShieldAlt, 
  FaClock,
  FaPiggyBank,
  FaUserFriends,
  FaGlobeAsia,
  FaTools,
  FaCheckCircle,
  FaArrowRight
} from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';

const Benefits = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: 'ease-out',
      once: true
    });
  }, []);

  // 비즈니스 혜택
  const businessBenefits = [
    {
      icon: <FaChartLine className="w-12 h-12 text-blue-500" />,
      title: '생산성 향상',
      description: '업무 효율성 증대',
      points: [
        '실시간 협업으로 업무 속도 향상',
        '통합 솔루션으로 업무 단순화',
        '자동화된 워크플로우',
        '시간과 비용 절감'
      ]
    },
    {
      icon: <FaHandshake className="w-12 h-12 text-green-500" />,
      title: '협업 강화',
      description: '팀워크 개선',
      points: [
        '실시간 문서 공동 작업',
        '원활한 의사소통',
        '프로젝트 진행 효율화',
        '팀 생산성 극대화'
      ]
    },
    {
      icon: <FaShieldAlt className="w-12 h-12 text-red-500" />,
      title: '보안 강화',
      description: '기업 데이터 보호',
      points: [
        '엔터프라이즈급 보안',
        '데이터 암호화',
        '접근 권한 관리',
        '감사 로그 및 보고서'
      ]
    },
    {
      icon: <FaClock className="w-12 h-12 text-yellow-500" />,
      title: '시간 절약',
      description: '업무 시간 단축',
      points: [
        '자동화된 작업 처리',
        '빠른 정보 검색',
        '효율적인 일정 관리',
        '신속한 의사결정'
      ]
    },
    {
      icon: <FaPiggyBank className="w-12 h-12 text-purple-500" />,
      title: '비용 절감',
      description: 'IT 비용 최적화',
      points: [
        '하드웨어 비용 절감',
        '유지보수 비용 감소',
        '라이선스 통합 관리',
        'TCO 절감 효과'
      ]
    },
    {
      icon: <FaUserFriends className="w-12 h-12 text-teal-500" />,
      title: '업무 유연성',
      description: '하이브리드 워크 지원',
      points: [
        '언제 어디서나 업무 가능',
        '디바이스 독립적 접근',
        '원격 근무 지원',
        '실시간 협업 환경'
      ]
    }
  ];

  // 공식 파트너 혜택
  const partnerBenefits = [
    {
      icon: <FaHandshake />,
      title: "특별 할인",
      description: "공식 파트너사를 통한 구매 시 일반 구매 대비 최대 10% 할인",
    },
    {
      icon: <FaGlobeAsia />,
      title: "확장된 무료 체험",
      description: "일반 14일에서 30일로 연장된 무료 체험 기간 제공",
    },
    {
      icon: <FaTools />,
      title: "전문 기술 지원",
      description: "구글 인증 전문가의 직접적인 기술 지원 및 구축 컨설팅",
    },
    {
      icon: <FaShieldAlt />,
      title: "안전한 전환",
      description: "기존 시스템에서 Google Workspace로의 안전한 마이그레이션 지원",
    },
  ];

  // 활용 사례
  const caseStudies = [
    {
      company: "스타트업 A사",
      industry: "IT 서비스",
      results: [
        "커뮤니케이션 비용 38% 절감",
        "프로젝트 완료 시간 45% 단축",
        "모바일 접근성으로 유연한 업무 환경 구축"
      ]
    },
    {
      company: "중견기업 B사",
      industry: "제조업",
      results: [
        "문서 관리 시간 60% 감소",
        "팀 간 정보 공유 속도 2배 향상",
        "서버 유지보수 비용 연간 3천만원 절감"
      ]
    },
    {
      company: "대기업 C사",
      industry: "금융 서비스",
      results: [
        "고급 보안 기능으로 규정 준수 강화",
        "회의 준비 시간 50% 감소",
        "직원 만족도 82% 증가"
      ]
    }
  ];

  return (
    <div className="bg-white overflow-hidden">
      {/* 히어로 섹션 */}
      <section className="relative bg-gradient-to-br from-blue-900 to-blue-700 text-white overflow-hidden">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 py-20 md:py-28 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-blue-800/50 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-blue-700/50">
              <img 
                src="https://www.gstatic.com/partners/badge/images/2022/PartnerBadgeClickable.svg" 
                alt="Google Partner" 
                className="h-8 mr-3"
              />
              <span className="text-sm font-medium">구글 공식 파트너</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              비즈니스 혁신을 위한<br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-white to-blue-300">
                특별한 혜택
              </span>
            </h1>
            
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-10">
              Google Workspace 도입을 통해 얻는 비즈니스 혜택과<br />
              공식 파트너사만의 특별한 서비스를 확인하세요.
            </p>
          </div>
        </div>
        
        {/* 물결 모양 디바이더 */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" fill="#ffffff">
            <path d="M0,96L48,85.3C96,75,192,53,288,53.3C384,53,480,75,576,80C672,85,768,75,864,64C960,53,1056,43,1152,48C1248,53,1344,75,1392,85.3L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* 공식 파트너 혜택 섹션 - 강조 표시 */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <div className="inline-flex mb-4">
              <span className="bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full">
                파트너 전용 혜택
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              구글 공식 파트너사만의 특별한 혜택
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Workway는 Google의 공식 파트너사로서 다음과 같은 독점적인 혜택을 제공합니다
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {partnerBenefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-xl shadow-lg p-8 transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl border border-gray-100"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-6 text-2xl">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-16 text-center" data-aos="fade-up">
            <a 
              href="http://pf.kakao.com/_jyebn/chat" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg flex items-center">
                30일 무료 체험 신청하기
                <FaArrowRight className="ml-2 text-sm" />
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* 비즈니스 혜택 섹션 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Google Workspace 비즈니스 혜택
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Google Workspace는 비즈니스 성과를 높이는 다양한 혜택을 제공합니다
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {businessBenefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl"
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                <div className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="mr-4">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-500">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                  
                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start">
                        <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI 섹션 */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl overflow-hidden shadow-xl" data-aos="fade-up">
            <div className="md:flex">
              <div className="md:w-1/2 p-8 md:p-12">
                <h2 className="text-3xl font-bold text-white mb-6">
                  Google Workspace의 투자 대비 효과
                </h2>
                <p className="text-white mb-8">
                  Forrester의 연구에 따르면 Google Workspace는 3년간 총 168%의 ROI를 제공하며, 
                  생산성 향상과 비용 절감 효과가 탁월합니다.
                </p>
                <a 
                  href="http://pf.kakao.com/_jyebn/chat" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <button className="bg-white text-blue-700 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-md flex items-center">
                    ROI 상세 분석 상담받기
                    <FaArrowRight className="ml-2 text-sm" />
                  </button>
                </a>
              </div>
              <div className="md:w-1/2 bg-blue-700 p-8 md:p-12">
                <div className="grid grid-cols-1 gap-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                    <p className="text-4xl font-bold text-white mb-2">168%</p>
                    <p className="text-blue-100">투자수익률(ROI)</p>
                  </div>
                  <div className="flex gap-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 flex-1">
                      <p className="text-3xl font-bold text-white mb-2">40%</p>
                      <p className="text-blue-100">생산성 향상</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 flex-1">
                      <p className="text-3xl font-bold text-white mb-2">45%</p>
                      <p className="text-blue-100">비용 절감</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 활용 사례 섹션 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              비즈니스 활용 사례
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              다양한 산업군의 기업들이 Google Workspace를 통해 혁신을 이루었습니다
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {caseStudies.map((caseStudy, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="bg-blue-50 p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {caseStudy.company}
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {caseStudy.industry}
                  </p>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 font-medium mb-4">주요 성과:</p>
                  <ul className="space-y-3">
                    {caseStudy.results.map((result, idx) => (
                      <li key={idx} className="flex items-start">
                        <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl" data-aos="fade-up">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            <div className="md:flex">
              <div className="md:w-3/5 p-8 md:p-12">
                <div className="inline-flex items-center bg-blue-50 text-blue-600 rounded-full px-3 py-1 text-sm font-medium mb-6">
                  공식 파트너 혜택
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  지금 바로 Google Workspace를 시작하세요
                </h2>
                <p className="text-gray-600 mb-8">
                  공식 파트너사 Workway와 함께 Google Workspace를 도입하고<br />
                  특별한 혜택과 전문 기술 지원을 받아보세요.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="http://pf.kakao.com/_jyebn/chat" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md flex items-center">
                      30일 무료로 시작하기
                    </button>
                  </a>
                  <a 
                    href="http://pf.kakao.com/_jyebn/chat" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <button className="bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center">
                      <img
                        src="https://img.icons8.com/color/24/000000/kakaotalk.png"
                        alt="카카오톡"
                        className="w-5 h-5 mr-2"
                      />
                      상담 문의하기
                    </button>
                  </a>
                </div>
              </div>
              <div className="md:w-2/5 bg-gradient-to-br from-blue-600 to-blue-800 p-8 md:p-12 flex items-center justify-center">
                <img 
                  src="https://www.gstatic.com/partners/badge/images/2022/PartnerBadgeClickable.svg" 
                  alt="Google Partner" 
                  className="max-w-full h-40"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Benefits;